/**
 * AnalyticsSummaryMetrics Component
 *
 * Displays summary metric cards above analytics charts.
 * Shows key statistics with trend indicators based on the selected view:
 * - Alert Trends: Total alerts, critical count, peak day, resolution rate
 * - Stock Levels: Total SKUs, health breakdown, out-of-stock count
 * 
 * Features animated transitions when switching between views.
 * Note: Filter controls have been moved to the tabs row in AnalyticsCard.
 */

import {
  InlineStack,
  BlockStack,
  Text,
  Badge,
  Box,
  Divider,
  SkeletonBodyText,
  Icon,
} from '@shopify/polaris';
import { ArrowUpIcon, ArrowDownIcon } from '@shopify/polaris-icons';
import type { AnalyticsView, AlertTrendsSummary, StockLevelsSummary, TimeRangePreset } from '../../types/analytics';

interface MetricCardProps {
  label: string;
  value: string | number;
  change?: number;
  changeSuffix?: string;
  tone?: 'success' | 'warning' | 'critical' | 'info' | undefined;
  isLoading?: boolean;
}

/**
 * Individual metric card showing a value with optional trend indicator
 */
function MetricCard({
  label,
  value,
  change,
  changeSuffix = '%',
  tone: _tone, // Accept but don't use tone for static badges
  isLoading = false,
}: MetricCardProps) {
  if (isLoading) {
    return (
      <div className="metric-card">
        <Box minWidth="100px">
          <BlockStack gap="100">
            <SkeletonBodyText lines={1} />
            <SkeletonBodyText lines={1} />
          </BlockStack>
        </Box>
      </div>
    );
  }

  // Determine badge properties based on change (only show for actual changes)
  let badgeTone: 'success' | 'warning' | 'critical' | 'info' | undefined;
  let showUpArrow = false;
  let showDownArrow = false;

  if (change !== undefined && change !== 0) {
    if (change > 0) {
      showUpArrow = true;
      // For metrics where increase is bad (like critical alerts), use critical tone
      if (label.toLowerCase().includes('critical') || label.toLowerCase().includes('out of stock')) {
        badgeTone = 'critical';
      } else {
        badgeTone = 'success';
      }
    } else {
      showDownArrow = true;
      if (label.toLowerCase().includes('critical') || label.toLowerCase().includes('out of stock')) {
        badgeTone = 'success';
      } else {
        badgeTone = 'info';
      }
    }
  }

  // Format change value as string
  const changeText = change !== undefined && change !== 0 
    ? `${Math.abs(change).toFixed(1)}${changeSuffix}`
    : '';

  return (
    <div className="metric-card">
      <Box minWidth="80px" paddingInline="200">
        <BlockStack gap="100">
          <Text as="span" variant="bodySm" tone="subdued">
            {label}
          </Text>
          <InlineStack gap="200" blockAlign="center" wrap={false}>
            <span className="metric-value">
              <Text as="span" variant="headingMd" fontWeight="bold">
                {typeof value === 'number' ? value.toLocaleString() : value}
              </Text>
            </span>
            {change !== undefined && change !== 0 && (
              <InlineStack gap="050" blockAlign="center" wrap={false}>
                {showUpArrow && <Icon source={ArrowUpIcon} tone={badgeTone === 'critical' ? 'critical' : 'success'} />}
                {showDownArrow && <Icon source={ArrowDownIcon} tone={badgeTone === 'success' ? 'success' : 'subdued'} />}
                <Badge tone={badgeTone} size="small">
                  {changeText}
                </Badge>
              </InlineStack>
            )}
          </InlineStack>
        </BlockStack>
      </Box>
    </div>
  );
}

export interface AnalyticsSummaryMetricsProps {
  /** Current active view */
  activeView: AnalyticsView;
  /** Alert trends summary data */
  alertSummary?: AlertTrendsSummary;
  /** Stock levels summary data */
  stockSummary?: StockLevelsSummary;
  /** Whether data is loading */
  isLoading?: boolean;
  /** Current time range (for display only - unused, kept for API compatibility) */
  timeRange?: TimeRangePreset;
}

/**
 * AnalyticsSummaryMetrics - Summary metrics row for analytics views
 * Note: Filter controls have been moved to the tabs row in AnalyticsCard
 */
export function AnalyticsSummaryMetrics({
  activeView,
  alertSummary,
  stockSummary,
  isLoading = false,
}: AnalyticsSummaryMetricsProps) {
  // Velocity view doesn't display summary metrics
  if (activeView === 'velocity') {
    return null;
  }

  if (activeView === 'alerts') {
    return (
      <div className="analytics-metrics-row metrics-enter" key={`alerts-${alertSummary?.totalAlerts ?? 0}`}>
        <Box
          background="bg-surface-secondary"
          padding="300"
          borderRadius="200"
        >
          <InlineStack gap="200" align="start" blockAlign="center" wrap>
            <MetricCard
              label="Total Alerts"
              value={alertSummary?.totalAlerts ?? 0}
              isLoading={isLoading}
            />
            <Divider />
            <MetricCard
              label="Critical"
              value={alertSummary?.criticalCount ?? 0}
              change={alertSummary?.criticalChange}
              isLoading={isLoading}
            />
            <Divider />
            <MetricCard
              label="Warning"
              value={alertSummary?.warningCount ?? 0}
              change={alertSummary?.warningChange}
              isLoading={isLoading}
            />
            <Divider />
            <MetricCard
              label="Avg/Day"
              value={alertSummary?.averageAlertsPerDay?.toFixed(1) ?? '0'}
              isLoading={isLoading}
            />
            {alertSummary?.peakAlertDay && (
              <>
                <Divider />
                <MetricCard
                  label="Peak Day"
                  value={alertSummary.peakAlertDay}
                  isLoading={isLoading}
                />
              </>
            )}
          </InlineStack>
        </Box>
      </div>
    );
  }

  if (activeView === 'inventory') {
    return (
      <div className="analytics-metrics-row metrics-enter" key={`inventory-${stockSummary?.totalSKUs ?? 0}`}>
        <Box
          background="bg-surface-secondary"
          padding="300"
          borderRadius="200"
        >
          <InlineStack gap="200" align="start" blockAlign="center" wrap>
            <MetricCard
              label="Total SKUs"
              value={stockSummary?.totalSKUs ?? 0}
              isLoading={isLoading}
            />
            <Divider />
            <MetricCard
              label="Healthy"
              value={`${stockSummary?.healthyPercentage?.toFixed(0) ?? 0}%`}
              change={stockSummary?.healthyChange}
              isLoading={isLoading}
            />
            <Divider />
            <MetricCard
              label="Warning"
              value={`${stockSummary?.warningPercentage?.toFixed(0) ?? 0}%`}
              isLoading={isLoading}
            />
            <Divider />
            <MetricCard
              label="Critical"
              value={`${stockSummary?.criticalPercentage?.toFixed(0) ?? 0}%`}
              change={stockSummary?.criticalChange}
              isLoading={isLoading}
            />
            <Divider />
            <MetricCard
              label="Out of Stock"
              value={stockSummary?.outOfStockCount ?? 0}
              isLoading={isLoading}
            />
            <Divider />
            <MetricCard
              label="Total Units"
              value={stockSummary?.totalUnits ?? 0}
              isLoading={isLoading}
            />
          </InlineStack>
        </Box>
      </div>
    );
  }

  return null;
}

export default AnalyticsSummaryMetrics;
